import { formatDate, isInPast, isToday } from '@seen/core/dates'
import type { TaskDueInboxItem } from '@seen/core/models/inbox'
import { Order } from '@seen/core/models/orders/Order'
import { Participant } from '@seen/core/models/patients/participant'
import { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { isOfType } from '@seen/core/utils'
import { cn } from '@seen/ui'
import type { JSX, ReactNode } from 'react'
import { IconDue } from '#src/components/icons/IconDue.tsx'
import { IconOrderStatus } from '#src/components/icons/IconOrderStatus.tsx'
import { IconTaskState } from '#src/components/icons/IconTaskState.tsx'
import { ParticipantTaskRowBadge } from '#src/components/ParticipantTaskRowBadge.tsx'
import { TaskPriorityIcon } from '#src/components/task/TaskPriorityIcon.tsx'
import { RowBadge } from '#src/components/TaskRowBadge.tsx'
import type { NotificationItem } from '#src/hooks/useNotificationItems.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { InboxRow } from '#src/pages/inbox/row/InboxRow.tsx'
import { InboxRowContent } from '#src/pages/inbox/row/InboxRowContent.tsx'
import { InboxRowSubtext } from '#src/pages/inbox/row/InboxRowSubtext.tsx'
import { InboxRowText } from '#src/pages/inbox/row/InboxRowText.tsx'
import { getOrderTitle } from '#src/utils/getOrderTitle.ts'
import { extractTextFromHTML } from '#src/utils/html.ts'

export interface TaskDueInboxItemRowProps {
  item: TaskDueInboxItem
  notificationItem: NotificationItem
  onSelect?: () => void
  isSelected?: boolean
  sidebarOpen?: boolean
}

export function TaskDueInboxItemRow({
  item,
  notificationItem,
  onSelect,
  isSelected,
  sidebarOpen,
}: TaskDueInboxItemRowProps): ReactNode {
  const { data: task, isLoading } = useRead(StandardTask, item.task)
  const { data: patient } = useRead(Participant, task?.participant)
  const { data: order } = useRead(
    Order,
    isOfType(task?.focus, 'ServiceRequest') ? task.focus : undefined
  )
  const actionUrl = task?.actionUrl ?? item.actionUrl

  const orderTitle = getOrderTitle(order)

  return (
    <InboxRow
      to={actionUrl}
      notificationItem={notificationItem}
      onSelect={onSelect}
      isSelected={isSelected}
      sidebarOpen={sidebarOpen}
      ghost={!!task?.isCompletedOrCancelled}
    >
      <InboxRowDueIcon task={task} />
      <InboxRowContent>
        <InboxRowText
          notificationItem={notificationItem}
          isLoading={isLoading}
          badges={
            <>
              {patient && (
                <ParticipantTaskRowBadge
                  className="[&>button]:h-[1.55rem] [&>button]:py-0"
                  participant={patient.reference}
                />
              )}
              {isOfType(task?.focus, 'ServiceRequest') && order && (
                <RowBadge
                  loading={!order}
                  icon={
                    <IconOrderStatus
                      status={order?.status}
                      isSubmitting={order?.isSubmitting}
                      className="size-3.5"
                    />
                  }
                >
                  {order?.seenIdentifier} {!!orderTitle && orderTitle}
                </RowBadge>
              )}
            </>
          }
        >
          {task?.priority && (
            <TaskPriorityIcon
              priority={task.priority}
              isTaskCompletedOrCancelled={task.isCompletedOrCancelled}
              className={cn('-mt-0.75 mr-0.75 inline')}
            />
          )}
          {task?.state && (
            <IconTaskState
              state={task.state}
              className="-mt-0.75 mr-0.75 inline"
            />
          )}
          <span>{extractTextFromHTML(task?.title ?? '[deleted]')}</span>
        </InboxRowText>
        <InboxRowSubtext>
          <DueLabel task={task} />
        </InboxRowSubtext>
      </InboxRowContent>
    </InboxRow>
  )
}

export function dueLabel(task: StandardTask | undefined): string {
  if (!task?.dueOn) {
    return 'Was due'
  }
  if (task.isDone) {
    return 'Completed'
  }
  if (task.isCompletedOrCancelled) {
    return 'Canceled'
  }
  if (isToday(task.dueOn)) {
    return 'Due today'
  }
  if (isInPast(task.dueOn)) {
    return 'Overdue'
  }
  return `Due ${formatDate(task.dueOn)}`
}

function DueLabel({ task }: { task: StandardTask | undefined }): ReactNode {
  return dueLabel(task)
}

function InboxRowDueIcon({
  task,
}: {
  task: StandardTask | undefined
}): JSX.Element {
  return (
    <div
      className={cn(
        'flex size-8 items-center justify-center rounded-full border border-red-300 bg-red-150',
        task?.isCompletedOrCancelled && 'border-gray-300 bg-gray-100'
      )}
    >
      <IconDue
        dateString={task?.dueOn ?? ''}
        isCompleted={task?.isCompletedOrCancelled ?? false}
        className="size-3.5"
      />
    </div>
  )
}
